import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useU2000WebSocketContext } from "../../context/websocket/U2000WebSocketContext";
import { useToast } from "../../context/toast/U2000ToastContext";
import { useU2000AuthContext } from "../../context/auth/U2000UseAuthContext";
import type { U2000JobCompletedMessage } from "../../types/U2000WebSocketMessages";
import {
  isJobCompletedMessage,
  isNotificationMessage,
  U3121_PROJECT_EXCEL_EXPORT_JOB_TYPE,
  U3125_AR_AGING_REPORT_EXCEL_JOB_TYPE,
  U3125_INVOICE_EXCEL_EXPORT_JOB_TYPE,
  U3130_TIMESHEET_APPROVAL_EXCEL_EXPORT_JOB_TYPE,
  U3130_TIMESHEET_EMPLOYEUR_D_EXCEL_EXPORT_JOB_TYPE,
  U3134_TEC_AGE_REPORT_EXCEL_JOB_TYPE,
  U3134_WIP_LINE_EXCEL_EXPORT_JOB_TYPE,
  U3141_LAND_REGISTER_EXCEL_EXPORT_JOB_TYPE,
  U3204_AI_EXCEL_EXPORT_JOB_TYPE,
} from "../../types/U2000WebSocketMessages";
import { u2000IsApiSameOriginFileUrl } from "../../tools/u2000IsApiSameOriginFileUrl";

// Set global pour tracker les jobs/notifications déjà téléchargés (évite les téléchargements multiples)
const downloadedJobs = new Set<string>();

// Compteur global pour tracker le nombre de hooks actifs (debug)
let activeHooksCount = 0;

/** jobTypes qui déclenchent toast + téléchargement auto (Excel uniquement ; rapports PDF : notification uniquement, pas d'ouverture auto) */
const EXPORT_JOB_TYPES_AUTO_DOWNLOAD = [
  U3141_LAND_REGISTER_EXCEL_EXPORT_JOB_TYPE,
  U3130_TIMESHEET_APPROVAL_EXCEL_EXPORT_JOB_TYPE,
  U3130_TIMESHEET_EMPLOYEUR_D_EXCEL_EXPORT_JOB_TYPE,
  U3125_AR_AGING_REPORT_EXCEL_JOB_TYPE,
  U3134_TEC_AGE_REPORT_EXCEL_JOB_TYPE,
  U3204_AI_EXCEL_EXPORT_JOB_TYPE,
  U3121_PROJECT_EXCEL_EXPORT_JOB_TYPE,
  U3134_WIP_LINE_EXCEL_EXPORT_JOB_TYPE,
  U3125_INVOICE_EXCEL_EXPORT_JOB_TYPE,
  // U3121_PROJECT_OPENING_FORM_PDF_EXPORT : exclu, l'utilisateur clique manuellement dans le centre de notifications
  // Autres Excel (U3120_*, U3101_*, etc.) : étendre au besoin
];

/**
 * Hook pour écouter les notifications de fin d'export (Excel, PDF) via WebSocket
 * Écoute job:completed (legacy) et notification:new (nouveau flux backend).
 * Affiche un toast et déclenche le téléchargement lorsque l'export est prêt.
 */
export function useU2000ExportExcelNotification() {
  const { t } = useTranslation();
  const toast = useToast();
  const { auth } = useU2000AuthContext();
  const { on, off } = useU2000WebSocketContext();

  const downloadFile = useCallback(
    (fileUrl: string, jobId: string, ext: "xlsx" | "pdf" = "xlsx") => {
      if (import.meta.env.DEV) {
        console.log(`[ExportExcel] Début téléchargement pour job ${jobId}, URL: ${fileUrl}`);
      }
      try {
        // BL-0513 : ne jamais fileUrl.startsWith("") (VITE_API_URL vide en prod)
        const isSameOrigin = u2000IsApiSameOriginFileUrl(fileUrl);
        if (import.meta.env.DEV) {
          console.log(`[ExportExcel] isSameOrigin: ${isSameOrigin}`);
        }
        if (isSameOrigin && auth.token) {
          if (import.meta.env.DEV) {
            console.log(`[ExportExcel] Fetch avec authentification pour job ${jobId}`);
          }
          const headers: Record<string, string> = { Authorization: `Bearer ${auth.token}` };
          const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || "";
          if (apiUrl.includes("ngrok") || window.location.hostname.includes("ngrok")) {
            headers["ngrok-skip-browser-warning"] = "1";
          }
          fetch(fileUrl, {
            method: "GET",
            headers,
            credentials: "include",
          })
            .then((response) => {
              if (import.meta.env.DEV) {
                console.log(`[ExportExcel] Réponse fetch pour job ${jobId}:`, response.status, response.statusText);
              }
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
              return response.blob();
            })
            .then((blob) => {
              if (import.meta.env.DEV) {
                console.log(`[ExportExcel] Blob reçu pour job ${jobId}, taille: ${blob.size} bytes`);
              }
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = `export_${jobId}.${ext}`;
              document.body.appendChild(link);
              if (import.meta.env.DEV) {
                console.log(`[ExportExcel] Clic sur lien de téléchargement pour job ${jobId}`);
              }
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              if (import.meta.env.DEV) {
                console.log(`[ExportExcel] Téléchargement terminé pour job ${jobId}`);
              }
            })
            .catch((error) => {
              console.error(`[ExportExcel] Erreur lors du téléchargement pour job ${jobId}:`, error);
              toast.error(
                t("msgExportExcelDownloadError", { jobId, ns: "common" }) || `Erreur lors du téléchargement (référence: ${jobId})`,
                t("errorTitle", { ns: "common" }) || "Erreur"
              );
            });
        } else {
          // URL S3 / externe : pas de JWT ; <a> plutôt que window.open (anti popup WebSocket)
          if (import.meta.env.DEV) {
            console.log(`[ExportExcel] Téléchargement URL externe via lien pour job ${jobId}`);
          }
          const link = document.createElement("a");
          link.href = fileUrl;
          link.download = `export_${jobId}.${ext}`;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (error) {
        console.error(`[ExportExcel] Erreur lors du téléchargement pour job ${jobId}:`, error);
        toast.error(
          t("msgExportExcelDownloadError", { jobId, ns: "common" }) || `Erreur lors du téléchargement (référence: ${jobId})`,
          t("errorTitle", { ns: "common" }) || "Erreur"
        );
      }
    },
    [auth.token, toast, t]
  );

  useEffect(() => {
    activeHooksCount++;
    if (import.meta.env.DEV) {
      console.log(`[ExportExcel] Hook monté - Abonnement à job:completed (Total hooks actifs: ${activeHooksCount})`);
    }

    const handleJobCompleted = (message: U2000JobCompletedMessage) => {
      if (import.meta.env.DEV) {
        console.log("[ExportExcel] Message job:completed reçu:", message);
      }

      if (!isJobCompletedMessage(message)) {
        if (import.meta.env.DEV) {
          console.warn("[ExportExcel] Message reçu n'est pas un job:completed valide:", message);
        }
        return;
      }

      const { jobId, status, result, errorMessage } = message;

      if (import.meta.env.DEV) {
        console.log(`[ExportExcel] Traitement job ${jobId}, status: ${status}`);
      }

      if (status === "completed") {
        // Vérifier si ce job a déjà été téléchargé
        if (downloadedJobs.has(jobId)) {
          if (import.meta.env.DEV) {
            console.log(`[ExportExcel] Job ${jobId} déjà téléchargé, ignoré`);
          }
          return;
        }

        // Marquer le job comme téléchargé
        downloadedJobs.add(jobId);
        
        // Nettoyer après 5 minutes pour éviter l'accumulation
        setTimeout(() => {
          downloadedJobs.delete(jobId);
        }, 5 * 60 * 1000);

        if (import.meta.env.DEV) {
          console.log(`[ExportExcel] Export réussi pour job ${jobId}, déclenchement téléchargement`);
        }

        // Export réussi
        toast.success(
          t("msgExportExcelReady", { jobId, ns: "common" }) || `Export prêt (référence: ${jobId})`,
          t("successTitle", { ns: "common" }) || "Succès"
        );

        // Déclencher le téléchargement si fileUrl est disponible
        if (result?.fileUrl) {
          if (import.meta.env.DEV) {
            console.log(`[ExportExcel] Téléchargement du fichier: ${result.fileUrl}`);
          }
          downloadFile(result.fileUrl, jobId, "xlsx");
        } else {
          console.warn("[ExportExcel] Message job:completed sans fileUrl:", message);
        }
      } else if (status === "failed") {
        if (import.meta.env.DEV) {
          console.log(`[ExportExcel] Export échoué pour job ${jobId}`);
        }
        // Export échoué
        const errorMsg = errorMessage || t("msgExportExcelFailed", { jobId, ns: "common" }) || `L'export a échoué (référence: ${jobId})`;
        toast.error(errorMsg, t("errorTitle", { ns: "common" }) || "Erreur");
      }
    };

    const handleNotificationNew = (message: unknown) => {
      if (!isNotificationMessage(message)) return;

      const metadata = message.metadata as Record<string, unknown> | undefined;
      const jobType = metadata?.jobType as string | undefined;
      if (typeof jobType !== "string" || !EXPORT_JOB_TYPES_AUTO_DOWNLOAD.includes(jobType)) return;

      const notificationId = message.id;
      if (downloadedJobs.has(notificationId)) return;

      const isSuccess = message.importance !== "error" && message.importance !== "critical";
      if (!isSuccess || !message.fileUrl) return;

      downloadedJobs.add(notificationId);
      setTimeout(() => downloadedJobs.delete(notificationId), 5 * 60 * 1000);

      if (import.meta.env.DEV) {
        console.log(`[ExportExcel] notification:new export reçue (${jobType}), téléchargement`);
      }
      toast.success(
        message.title || (t("msgExportExcelReady", { jobId: notificationId, ns: "common" }) || "Export prêt"),
        t("successTitle", { ns: "common" }) || "Succès"
      );
      const ext = message.fileMimeType === "application/pdf" ? "pdf" : "xlsx";
      downloadFile(message.fileUrl as string, notificationId, ext);
    };

    const unsubJob = on("job:completed", handleJobCompleted);
    const unsubNotif = on("notification:new", handleNotificationNew);

    if (import.meta.env.DEV) {
      console.log("[ExportExcel] Abonnements créés (job:completed + notification:new)");
    }

    return () => {
      activeHooksCount--;
      unsubJob();
      unsubNotif();
      if (import.meta.env.DEV) {
        console.log(`[ExportExcel] Hook démonté (Total hooks actifs: ${activeHooksCount})`);
      }
    };
  }, [on, off, toast, t, auth.token, downloadFile]);
}
